/**
 * magnet-flow.js — the visual conversation flow builder for client-saas /magnets.
 *
 * A dedicated modal (opened from a magnet card's "Flow" button) that renders the magnet's
 * conversation as a vertical canvas of connected node-cards — one per step, type-colored,
 * showing a live preview of the prompt + choices. Cards drag-to-reorder (native HTML5 DnD
 * with drop indicators); click a card to expand it into an inline editor; add steps from a
 * type palette. Changed routes join the modal's atomic, revision-checked PATCH.
 *
 * Array order defines fall-through; single-select choices can select another step.
 * IDs remain stable and destinations are validated before saving.
 */

import AuthController from '../services/AuthController.js';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { LEAD_CAPTURE_FIELDS, STEP_TYPES, validateMagnetFlow, createAdditionalAnswer, automaticAnswerLabel, answerLabelForStep } from '@sse/shared';
import { guardedFetch } from '../core/navGeneration.js';
import { mergeEdited, sameValue } from './magnet-draft.js';
import { enhanceFlowDisclosures, rememberFlowDisclosures, updateFlowDisclosure, revealFlowControl } from './magnet-flow-disclosure.js';
import '../styles/magnet-flow-workspace.css';
import { flowStepTitle, flowStepHints, matchesFlowSearch } from './magnet-flow-outline.js';
import { updateAnswerDelivery } from './magnet-answer-settings.js';

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const toast = (message, type = 'info') => { try { PubSub.publish(AppBroadcast.UI_TOAST, { message, type }); } catch { /* no-op */ } };
const apiBase = () => AuthController.getConfig?.().BASE_URL || '';

// step type → node icon + accent color (client-only presentation; the value set and
// label come from the shared STEP_TYPES, so magnets.js's server-side validation and
// this editor's dropdown can't drift the way they already did once — same shape of
// bug as LEAD_CAPTURE_FIELDS/KNOWN_FIELDS and the support_topics defaults).
// darkColor: the light-mode `color` values are chosen to read on --color-bg white;
// 5 of the 7 measure under the 4.5:1 AA minimum against --color-bg dark (#091620) —
// e.g. single_select's #0072BA is ~3.6:1. darkColor is a brightened per-type
// replacement (computed, not eyeballed) used only when [data-theme="dark"].
const TYPE_ICON_COLOR = {
  single_select: { icon: 'quiz', color: '#0072BA', darkColor: '#38BDF8' },
  multi_select: { icon: 'checklist', color: '#0E6BA8', darkColor: '#22D3EE' },
  scheduler: { icon: 'event', color: '#915E8B', darkColor: '#E879F9' },
  text: { icon: 'short_text', color: '#5B54C6', darkColor: '#A78BFA' },
  email: { icon: 'mail', color: '#0E8F86', darkColor: '#2DD4BF' },
  phone: { icon: 'call', color: '#2E8B57', darkColor: '#4ADE80' },
  message: { icon: 'chat', color: '#B4791F', darkColor: '#FBBF24' },
};
const TYPE_META = {};
STEP_TYPES.forEach((t) => {
  TYPE_META[t.value] = { label: t.label, ...(TYPE_ICON_COLOR[t.value] || { icon: 'quiz', color: '#0072BA', darkColor: '#38BDF8' }) };
});
const TYPE_ORDER = STEP_TYPES.map((t) => t.value);
const QUESTION_PURPOSE = {
  text: ['Ask a question', 'Visitors type their own answer.'],
  single_select: ['Offer a choice', 'Visitors choose one answer or a path.'],
  multi_select: ['Offer several choices', 'Visitors can select more than one answer.'],
  email: ['Ask for an email', 'Collect an email address for follow-up.'],
  phone: ['Ask for a phone number', 'Collect a number for follow-up.'],
  scheduler: ['Request a preferred time', 'Your team confirms availability.'],
  message: ['Show a message', 'Share information without asking a question.'],
};
// Editable per-CTA routes (shown only when the magnet has that CTA). chat = mag_macro_steps.
const ROUTE_LABEL = { chat: 'Chat / default', book: 'Book (scheduler)', support: 'Support (intake)' };
const meta = (t) => {
  const m = TYPE_META[t] || TYPE_META.single_select;
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return isDark ? { ...m, color: m.darkColor } : m;
};
const needsOptions = (v) => v === 'single_select' || v === 'multi_select';
const needsField = (v) => v !== 'message';
const needsSchedule = (v) => v === 'scheduler';
// Only a message step can be terminal (matches the SDK: renderTerminal() only ever
// fires from the `type === 'message'` branch of advance()).
const canBeTerminal = (v) => v === 'message';
// scheduler has no dedicated real column to default to (see the Saves-to dropdown
// below) — left blank so the person building the flow has to pick one explicitly,
// same as the "choose which field this answer saves to" validation already requires.
const defaultFieldFor = (v) => (v === 'email' ? 'email' : v === 'phone' ? 'phone' : '');
const defaultSchedule = () => ({ meeting_label: '', start: '09:00', end: '17:00', mins: 30, days: 14 });
// step_config is a shared per-step bag, and this editor only owns PART of it. These are
// the keys the scheduler card renders; everything else in there belongs to a feature the
// flow editor has no UI for and must survive an edit round-trip untouched:
//   terminal / cta        — a message step's endpoint card + outbound link (SDK renderTerminal)
//   placeholder, optional — input-step knobs (SDK renderInput)
//   skip_if_prefilled     — auto-advance when the host already supplied the column (SDK advance)
// Treating the bag as scheduler-only is what silently deleted authored CTAs on save.
const SCHEDULE_KEYS = ['meeting_label', 'start', 'end', 'mins', 'days'];
// The terminal card now HAS a UI (below), so these are owned by this editor too — but
// shedding them still has to leave the rest of the bag alone, same as SCHEDULE_KEYS.
const TERMINAL_KEYS = ['terminal', 'cta'];
// Shed the named keys, keep the rest. null (not {}) when nothing is left, so an
// ordinary step still serializes without an empty step_config riding along.
function configWithout(cfg, dropKeys) {
  const out = {};
  Object.keys(cfg && typeof cfg === 'object' ? cfg : {}).forEach((k) => {
    if (!dropKeys.includes(k)) out[k] = cfg[k];
  });
  return Object.keys(out).length ? out : null;
}
function configWithoutSchedule(cfg) {
  return configWithout(cfg, SCHEDULE_KEYS);
}
// icons the SDK can actually render (must match its MPATH registry); '' = none. Friendly labels.
const OPTION_ICONS = [
  ['', 'No icon'], ['event', 'Calendar'], ['schedule', 'Clock'], ['mail', 'Email'], ['sms', 'Text'],
  ['phone', 'Phone'], ['chat', 'Chat'], ['description', 'Document'], ['help', 'Question'], ['place', 'Location'],
  ['person', 'Person'], ['bolt', 'Quick'], ['check_circle', 'Check'], ['star', 'Star'], ['home', 'Home'], ['build', 'Service'],
];

function hexToRgba(hex, a) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || '').trim());
  if (!m) return `rgba(0,114,186,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
}

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-flow-styles';
  style.textContent = `
    .mag-modal.mag-flow { width: min(680px, 94vw); }
    .flow-routes { display: flex; align-items: center; gap: 10px; margin: 4px 0 14px; padding: 10px 12px; border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); background: var(--color-surface, #F4F7FB); }
    .flow-routes__lbl { font-size: 0.78rem; font-weight: 700; color: var(--color-text-muted, #4A5A78); }
    .flow-routes select { flex: 1 1 auto; }
    .flow-canvas { padding: 2px 0; }
    .flow-start { display: flex; align-items: center; gap: 7px; justify-content: center; font-size: 0.74rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); }
    .flow-start .material-symbols-outlined { font-size: 17px; }
    .flow-conn { display: flex; flex-direction: column; align-items: center; }
    .flow-conn__line { width: 2px; height: 12px; background: var(--color-border, #D4DCE8); }
    .flow-conn .material-symbols-outlined { font-size: 16px; color: var(--color-text-subtle, #6C7C9A); margin-top: -5px; }

    .flow-node { position: relative; border: 1px solid var(--color-border, #D4DCE8); border-left: 3px solid var(--nc, #0072BA); border-radius: var(--radius-md, 10px); background: var(--color-bg, #fff); box-shadow: 0 1px 2px rgba(11,18,32,0.04); transition: box-shadow .12s, border-color .12s, opacity .12s; }
    .flow-node[draggable="true"] { cursor: grab; }
    .flow-node[draggable="true"]:hover { box-shadow: 0 3px 12px rgba(11,18,32,0.09); }
    .flow-node.dragging { opacity: 0.4; }
    .flow-node--open { box-shadow: 0 8px 26px rgba(11,18,32,0.14); }
    .flow-node.drop-before::before, .flow-node.drop-after::after { content: ""; position: absolute; left: 8px; right: 8px; height: 3px; border-radius: 3px; background: var(--color-primary-600, #0072BA); }
    .flow-node.drop-before::before { top: -8px; }
    .flow-node.drop-after::after { bottom: -8px; }
    .flow-node__hd { display: flex; align-items: center; gap: 8px; padding: 9px 11px; }
    .flow-node__grip { flex: 0 0 auto; display: grid; place-items: center; color: var(--color-text-subtle, #6C7C9A); }
    .flow-node__grip .material-symbols-outlined { font-size: 18px; }
    .flow-node__ico { flex: 0 0 auto; width: 26px; height: 26px; border-radius: 7px; display: grid; place-items: center; background: var(--nct, rgba(0,114,186,0.14)); color: var(--nc, #0072BA); }
    .flow-node__ico .material-symbols-outlined { font-size: 17px; }
    .flow-node__type { font-weight: 700; font-size: 0.82rem; color: var(--nc, #0072BA); }
    .flow-node__n { font-size: 0.72rem; color: var(--color-text-subtle, #6C7C9A); }
    .flow-node__tools { margin-left: auto; display: flex; gap: 3px; }
    .flow-node__tools button { appearance: none; border: 1px solid transparent; background: transparent; cursor: pointer; width: 28px; height: 28px; border-radius: 7px; display: grid; place-items: center; color: var(--color-text-subtle, #6C7C9A); }
    .flow-node__tools button:hover:not(:disabled) { background: var(--color-surface, #F4F7FB); color: var(--color-text, #0B1220); }
    .flow-node__tools button:disabled { opacity: 0.35; cursor: default; }
    .flow-node__tools button .material-symbols-outlined { font-size: 17px; }
    .flow-node__preview { padding: 0 12px 12px 46px; cursor: pointer; }
    .flow-node__prompt { font-size: 0.86rem; color: var(--color-text, #0B1220); overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    .flow-node__prompt.is-empty { color: var(--color-text-subtle, #6C7C9A); font-style: italic; }
    .flow-node__meta { font-size: 0.74rem; color: var(--color-text-subtle, #6C7C9A); margin-top: 5px; }
    .flow-node__meta b { color: var(--color-text-muted, #4A5A78); font-weight: 700; }
    .flow-node__chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 7px; }
    .flow-node__chip { font-size: 0.72rem; font-weight: 600; padding: 3px 9px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }
    .flow-node__body { padding: 4px 14px 14px; border-top: 1px solid var(--color-border, #D4DCE8); }
    .flow-node__body .mag-modal__label:first-child { margin-top: 8px; }
    .flow-sub { font-weight: 400; font-size: 0.72rem; color: var(--color-text-subtle, #6C7C9A); }
    /* "optional" flips to "required" once either half of the CTA pair is filled. */
    .flow-sub--req { color: var(--color-danger, #DD4124); font-weight: 600; }
    .flow-cta-warn { margin: 6px 0 0; font-size: 0.76rem; font-weight: 600; color: var(--color-danger, #DD4124); }
    .flow-cta-warn[hidden] { display: none; }
    .flow-f-invalid { border-color: var(--color-danger, #DD4124) !important; }
    .flow-f-invalid:focus { outline-color: var(--color-danger, #DD4124); }
    /* The save error sits ABOVE the canvas, not after the palette where it used to —
       a validation nobody scrolls to is a validation nobody sees. */
    #flow-error:not(:empty) { margin: 0 0 12px; padding: 9px 12px; border-radius: var(--radius-md, 10px); background: var(--color-danger-soft, #FDECEA); border: 1px solid var(--color-danger, #DD4124); font-weight: 600; }
    .flow-opts { display: flex; flex-direction: column; gap: 6px; }
    .flow-opt { display: grid; grid-template-columns: minmax(0, 1fr) 100px 30px; gap: 6px; align-items: center; padding-bottom: 12px; }
    .flow-opt .flow-o-label { min-width: 0; }
    .flow-opt .flow-o-icon { width: 100%; }
    .flow-opt .flow-o-description,.flow-opt>label { grid-column: 1 / -1; min-width: 0; font-size: .8rem; }
    .flow-setting { display: flex; align-items: flex-start; gap: 8px; margin: 10px 0; font-size: 12px; line-height: 1.45; color: var(--color-text-muted, #4A5A78); }
    .flow-setting input { margin-top: 2px; }
    .flow-opt__del { appearance: none; border: 0; background: transparent; cursor: pointer; color: var(--color-text-subtle, #6C7C9A); width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; flex: 0 0 auto; }
    .flow-opt__del:hover:not(:disabled) { color: var(--color-danger, #DD4124); background: var(--color-surface-2, #EAF0F7); }
    .flow-opt__del:disabled { opacity: 0.35; cursor: default; }
    .mag-flow__addopt { appearance: none; border: 1px dashed var(--color-border, #D4DCE8); background: transparent; cursor: pointer; font: inherit; font-size: 0.86rem; font-weight: 600; color: var(--color-accent, #0072BA); padding: 9px 14px; border-radius: var(--radius-md, 10px); display: inline-flex; align-items: center; gap: 6px; margin-top: 6px; }
    .mag-flow__addopt:hover { background: var(--color-accent-soft, rgba(0,114,186,0.08)); }
    .mag-flow__addopt .material-symbols-outlined { font-size: 18px; }

    .flow-palette { margin-top: 16px; border-top: 1px dashed var(--color-border, #D4DCE8); padding-top: 14px; }
    .flow-palette__t { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); margin: 0 0 8px; }
    .flow-palette__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(128px, 1fr)); gap: 8px; }
    .flow-palette__chip { display: flex; align-items: center; gap: 7px; padding: 9px 11px; border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); background: var(--color-bg, #fff); cursor: pointer; font: inherit; font-size: 0.82rem; font-weight: 600; color: var(--color-text, #0B1220); text-align: left; }
    .flow-palette__chip:hover { border-color: var(--pc, #0072BA); background: var(--color-surface, #F4F7FB); }
    .flow-palette__chip .material-symbols-outlined { font-size: 18px; color: var(--pc, #0072BA); }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

// ── working state (one editor open at a time) ────────────────────────────────
let stepsModel = [];
let openUid = null;
let uidSeq = 0;
let dragUid = null;
let dropInfo = null;
// Hoisted so the exported validate/save/preview helpers (called from the host edit
// modal's footer) reach the same working model the mounted editor mutates.
let currentRoute = 'chat';
let flowRouteModels = {};
let flowRouteKeys = [];
let flowMagnet = null;
let flowOnDirty = null;
let flowBaselines = {};
let flowOptions = {};
let disclosureState = new Map();
let routeOpenSteps = {};
let outlineAttentionOnly = false;
let insertAfterUid = null;
const uid = () => 'f' + (++uidSeq);

function blankStep(type = 'single_select') {
  const step = {
    _uid: uid(),
    id: crypto.randomUUID(), // assigned on creation and retained across all edits
    step_type: type,
    step_prompts: [''],
    step_options: needsOptions(type) ? [{ label: '', goto: '', icon: '' }, { label: '', goto: '', icon: '' }] : [],
    step_columns: defaultFieldFor(type) ? [defaultFieldFor(type)] : [],
    step_description: '',
    step_config: needsSchedule(type) ? defaultSchedule() : null,
  };
  if (flowOptions.answerAuthoring && needsField(type) && !defaultFieldFor(type)) {
    const answer = createAdditionalAnswer(step, crypto.randomUUID());
    step.step_columns = [answer.key]; step.step_answer = answer.metadata;
  }
  return step;
}

// One raw step array → an editable model (adds _uid, normalizes shapes).
function modelFromSteps(src) {
  const models = (Array.isArray(src) ? src : []).map((s) => {
    const model = {
    _uid: uid(),
    id: s.id != null ? String(s.id) : '',
    step_type: TYPE_META[s.step_type] ? s.step_type : 'single_select',
    step_prompts: Array.isArray(s.step_prompts) ? s.step_prompts.map(String) : [],
    step_options: (Array.isArray(s.step_options) ? s.step_options : []).map((o) => ({
      ...JSON.parse(JSON.stringify(o || {})),
      label: o && o.label != null ? String(o.label) : '',
      goto: o && o.goto != null ? String(o.goto) : '',
      icon: o && o.icon != null ? String(o.icon) : '',
      description: o && o.description != null ? String(o.description) : '',
    })),
    step_columns: Array.isArray(s.step_columns) ? s.step_columns.map(String) : [],
    ...(s.step_answer !== undefined ? { step_answer: structuredClone(s.step_answer) } : {}),
    step_description: s.step_description != null ? String(s.step_description) : '',
    // Scheduler steps get the defaults merged underneath whatever was authored; every
    // other type keeps its bag verbatim. (Merging defaultSchedule() in unconditionally
    // stamped five dead scheduler keys onto e.g. a message step's { terminal, cta }.)
    step_config: needsSchedule(s.step_type)
      ? { ...defaultSchedule(), ...(s.step_config && typeof s.step_config === 'object' ? s.step_config : {}) }
      : ((s.step_config && typeof s.step_config === 'object') ? { ...s.step_config } : null),
    };
    model._original = JSON.parse(JSON.stringify(s));
    return model;
  });
  // Allocate missing working IDs across the entire route. Serializing each step
  // separately would assign the same ID to every legacy step without one.
  const baseline = serializeSteps(models);
  models.forEach((model, index) => { model._baseline = baseline[index]; });
  return models;
}

// The shared name→email capture tail the SDK appends to the built-in book/support routes.
function captureTailModel() {
  return [
    { _uid: uid(), id: '', step_type: 'text', step_prompts: ['Great, and your name?'], step_options: [], step_columns: ['first_name'], step_description: '', step_config: null },
    { _uid: uid(), id: '', step_type: 'email', step_prompts: ['The best email to reach you?'], step_options: [], step_columns: ['email'], step_description: '', step_config: null },
  ];
}

// Seed a book/support route the FIRST time it's opened, mirroring the SDK's built-in defaults
// (support options come from the magnet's Support CTA config when it has any).
function seedRouteModel(route, magnet) {
  if (route === 'book') {
    // Match the runtime's preferred-time mapping; custom fields are retained in notes.
    const cfg = (magnet.actions || []).find(a => a?.type === 'book')?.config || {};
    const remote = cfg.source === 'ms_bookings' && cfg.business_id && cfg.service_id;
    const schedule = remote ? { meeting_label: cfg.event_name || cfg.meeting_label || '', remote: 'ms_bookings' }
      : { meeting_label: cfg.meeting_label || '', start: cfg.start || '09:00', end: cfg.end || '17:00', mins: cfg.mins || 30, days: cfg.days || 14 };
    return [{ _uid: uid(), id: '', step_type: 'scheduler', step_prompts: [cfg.heading || 'When works best for you?'], step_options: [], step_columns: [cfg.column || 'preferred_time'], step_description: '', step_config: schedule }, ...captureTailModel()];
  }
  if (route === 'support') {
    const supp = (magnet.actions || []).find((a) => a && a.type === 'support');
    const cfgOpts = (supp && supp.config && Array.isArray(supp.config.options)) ? supp.config.options : [];
    const options = (cfgOpts.length ? cfgOpts.map((o) => ({ label: (o && o.label) || String(o), goto: '', icon: (o && o.icon) || '' }))
      : ['Sales question', 'Support', 'Billing', 'Something else'].map((l) => ({ label: l, goto: '', icon: '' })));
    return [{ _uid: uid(), id: '', step_type: 'multi_select', step_prompts: [supp?.config?.heading || 'How can we help you today?'], step_options: options, step_columns: [supp?.config?.column || 'support_topics'], step_description: 'Select all that apply', step_config: null }, ...captureTailModel()];
  }
  return [blankStep('single_select')];
}

// Build the working model for a route: authored steps if present, else the seed (book/support)
// or a blank first step (chat).
function loadRouteModel(route, magnet) {
  const authored = route === 'chat' ? magnet.steps : (magnet.routes && magnet.routes[route]);
  const model = modelFromSteps(authored);
  if (model.length) return model;
  // Baseline generated paths through the same projection as authored paths.
  // Otherwise opening Support adds empty option descriptions and looks edited.
  return route === 'chat' ? [blankStep('single_select')] : modelFromSteps(serializeSteps(seedRouteModel(route, magnet)));
}

// keep options/field/config in sync with a step's type after a type change
function reconcileStep(step) {
  if (needsOptions(step.step_type)) {
    if (!step.step_options.length) step.step_options = [{ label: '', goto: '', icon: '' }, { label: '', goto: '', icon: '' }];
  } else {
    step.step_options = [];
  }
  if (needsField(step.step_type)) {
    if (!step.step_columns.length && step._retainedAnswer) {
      step.step_columns = step._retainedAnswer.columns;
      if (step._retainedAnswer.metadata) step.step_answer = step._retainedAnswer.metadata;
      delete step._retainedAnswer;
    }
    if (flowOptions.answerAuthoring && !step.step_columns.length && !defaultFieldFor(step.step_type) && !needsOptions(step.step_type)) {
      const answer = createAdditionalAnswer(step, crypto.randomUUID());
      step.step_columns = [answer.key]; step.step_answer = answer.metadata;
    }
    if (!step.step_columns.length && defaultFieldFor(step.step_type)) step.step_columns = [defaultFieldFor(step.step_type)];
  } else {
    if (step.step_columns.length) step._retainedAnswer = {
      columns: step.step_columns.slice(), metadata: step.step_answer ? structuredClone(step.step_answer) : null,
    };
    step.step_columns = [];
    delete step.step_answer;
  }
  // A type change sheds the OTHER type's knobs, not the whole bag: dropping to null here
  // meant one mis-click on the type dropdown (even undone immediately) permanently deleted
  // an authored terminal CTA. Leaving an inert key behind is the cheaper mistake.
  step.step_config = needsSchedule(step.step_type)
    ? { ...defaultSchedule(), ...(step.step_config || {}) }
    : configWithoutSchedule(step.step_config);
}

// ── markup ───────────────────────────────────────────────────────────────────
function optionRowHTML(opt, only, step) {
  return `<div class="flow-opt">
    <input class="mag-modal__input flow-o-label" type="text" value="${esc(opt.label)}" placeholder="Option label">
    <select class="mag-modal__select flow-o-icon" title="Icon">${opt.icon && !OPTION_ICONS.some(([v]) => v === opt.icon) ? `<option selected value="${esc(opt.icon)}">${esc(opt.icon)}</option>` : ''}${OPTION_ICONS.map(([v, l]) =>
      `<option value="${esc(v)}"${v === opt.icon ? ' selected' : ''}>${esc(l)}</option>`).join('')}</select>
    <button class="flow-opt__del" type="button" data-x="delopt" title="Remove choice"${only ? ' disabled' : ''}><span class="material-symbols-outlined" aria-hidden="true">close</span></button>
    <input class="mag-modal__input flow-o-description" aria-label="Choice description" value="${esc(opt.description || '')}" placeholder="Description (optional)">
    ${step.step_type === 'single_select' ? `<label>After this choice
      <select class="mag-modal__select flow-o-goto" aria-label="Choice destination">
        <option value="">Next step in order</option>
        ${opt.goto && !stepsModel.some(s => s.id === opt.goto) ? `<option selected value="${esc(opt.goto)}">Missing step: ${esc(opt.goto)}</option>` : ''}
        ${stepsModel.filter(s => s !== step).map(s => `<option value="${esc(s.id)}"${s.id === opt.goto ? ' selected' : ''}>Step ${stepsModel.indexOf(s) + 1}: ${esc(flowStepTitle(s))}</option>`).join('')}
        ${opt.goto === step.id ? `<option selected value="${esc(step.id)}">This step (invalid loop)</option>` : ''}
      </select></label>` : ''}
  </div>`;
}

function editorBodyHTML(step) {
  const typeOpts = TYPE_ORDER.map((t) => `<option value="${t}"${t === step.step_type ? ' selected' : ''}>${esc(meta(t).label)}</option>`).join('');
  const only = step.step_options.length <= 1;
  return `<div class="flow-node__body">
    <label class="mag-modal__label">Step type</label>
    <select class="mag-modal__select flow-f-type">${typeOpts}</select>

    <label class="mag-modal__label">${step.step_type === 'message' ? 'Message' : 'Question'} <span class="flow-sub">one prompt per line; the final line is the question</span></label>
    <textarea class="mag-modal__input flow-f-prompts" rows="2" placeholder="${step.step_type === 'message' ? 'Thanks! One moment…' : 'What can we help you with?'}">${esc(step.step_prompts.join('\n'))}</textarea>
    <label class="mag-modal__label">Shown as
      <select class="mag-modal__input flow-f-presentation">
        <option value=""${!step.step_config?.presentation ? ' selected' : ''}>Automatic: ${step.step_type === 'message' ? 'messages from the magnet' : 'a question heading'}</option>
        <option value="bubbles"${step.step_config?.presentation === 'bubbles' ? ' selected' : ''}>Messages from the magnet</option>
        <option value="heading"${step.step_config?.presentation === 'heading' ? ' selected' : ''}>A question heading</option>
      </select>
    </label>
    <p class="mag-modal__hint">Applies to inline and pop-up cards. Chat panels always show messages.</p>

    ${canBeTerminal(step.step_type) ? `
    <div class="mag-tog-row" style="margin-top:10px">
      <input type="checkbox" class="flow-f-terminal" id="flow-f-terminal-${step._uid}"${step.step_config?.terminal ? ' checked' : ''}>
      <label for="flow-f-terminal-${step._uid}">End the conversation here (answers are not sent)</label>
    </div>
    <p class="mag-modal__hint">Leave this off to continue to the next step. Turn it on for an answer or a pointer that ends the conversation; nothing is sent, and you can add a button below.</p>
    ${step.step_config?.terminal ? `
    <label class="mag-modal__label">Link label <span class="flow-sub flow-cta-req">optional</span></label>
    <input class="mag-modal__input flow-f-cta-label" type="text" value="${esc(step.step_config?.cta?.label || '')}" placeholder="e.g. Visit our site">
    <label class="mag-modal__label">Link URL <span class="flow-sub flow-cta-req">optional</span></label>
    <input class="mag-modal__input flow-f-cta-url" type="url" value="${esc(step.step_config?.cta?.url || '')}" placeholder="https://example.com">
    <p class="flow-cta-warn" hidden>Both are needed to show the button — fill in the other one, or clear this one.</p>
    <p class="mag-modal__hint">Fill in both to show a button after this message, or leave both blank to just end the conversation. Opens in a new tab.</p>` : ''}` : ''}

    ${needsField(step.step_type) ? `
    <label class="mag-modal__label">Saves to</label>
    <select class="mag-modal__select flow-f-field">
      <option value=""${step.step_columns[0] ? '' : ' selected'}${needsOptions(step.step_type) ? '' : ' disabled'}>${needsOptions(step.step_type) ? 'Navigation only — do not save an answer' : 'Choose a lead field…'}</option>
      ${step.step_columns[0] && !LEAD_CAPTURE_FIELDS.some(f => f.value === step.step_columns[0]) ? `<option selected value="${esc(step.step_columns[0])}">${esc(step.step_columns[0])} (existing mapping)</option>` : ''}
      ${flowOptions.answerAuthoring && !step.step_answer?.id ? '<option value="__additional_answer__">Additional answer — keep with this inquiry</option>' : ''}
      ${LEAD_CAPTURE_FIELDS.map((f) => `<option value="${esc(f.value)}"${f.value === step.step_columns[0] ? ' selected' : ''}>${esc(f.label)}</option>`).join('')}
    </select>
    <p class="mag-modal__hint">${step.step_columns[0] && !LEAD_CAPTURE_FIELDS.some(f => f.value === step.step_columns[0]) ? 'This answer is saved in the lead’s notes.' : 'Each question should use its own field.'}</p>
    <label class="flow-setting"><input type="checkbox" class="flow-f-overwrite"${step.step_config?.allow_overwrite === true ? ' checked' : ''}> Allow this step to replace an earlier answer in this field</label>` : ''}

    ${flowOptions.assistantAuthoring && (['text', 'email', 'phone'].includes(step.step_type) || step.step_config?.group_with_previous) ? `
    <div class="flow-question-group">
      <label class="mag-modal__label"><input type="checkbox" class="flow-f-group"${step.step_config?.group_with_previous ? ' checked' : ''}${!step.step_config?.group_with_previous && !['text','email','phone'].includes(stepsModel[stepsModel.indexOf(step)-1]?.step_type) ? ' disabled' : ''}> Show with the previous question</label>
      <p class="mag-modal__hint">Group related text, email and phone fields on one screen.</p>
      <label class="mag-modal__label">Section heading <input class="mag-modal__input flow-f-group-heading" maxlength="120" value="${esc(step.step_config?.group_heading || '')}" placeholder="Your details"></label>
      <p class="mag-modal__hint">Used when this question starts a group.</p>
    </div>` : ''}
    ${['text', 'email', 'phone'].includes(step.step_type) ? `
    <label class="mag-modal__label"><input type="checkbox" class="flow-f-optional"${step.step_config?.optional ? ' checked' : ''}> Optional question</label>
    <label class="mag-modal__label">Skip label <input class="mag-modal__input flow-f-skip-label" value="${esc(step.step_config?.skip_label || '')}" placeholder="Skip"></label>
    <label class="mag-modal__label">Placeholder <input class="mag-modal__input flow-f-placeholder" value="${esc(step.step_config?.placeholder || '')}" placeholder="Default for this question type"></label>` : ''}
    ${needsField(step.step_type) ? `<label class="flow-setting"><input type="checkbox" class="flow-f-prefilled"${step.step_config?.skip_if_prefilled ? ' checked' : ''}> Skip when this answer is already available</label>` : ''}

    ${needsOptions(step.step_type) ? `
    <label class="mag-modal__label">Choices${step.step_type === 'multi_select' ? ' <span class="flow-sub">visitor can pick several</span>' : ''}</label>
    <div class="flow-opts">${step.step_options.map((o) => optionRowHTML(o, only, step)).join('')}</div>
    <button class="mag-flow__addopt" type="button" data-x="addopt"><span class="material-symbols-outlined" aria-hidden="true">add</span> Add choice</button>` : ''}

    ${needsSchedule(step.step_type) ? `
    <label class="mag-modal__label">Meeting label <span class="flow-sub">optional</span></label>
    <input class="mag-modal__input flow-f-mtlabel" type="text" value="${esc(step.step_config?.meeting_label || '')}" placeholder="e.g. 15 minute call">
    <div class="mag-modal__row" style="margin-top:8px">
      <div><label class="mag-modal__label">From</label><input class="mag-modal__input flow-f-start" type="time" value="${esc(step.step_config?.start || '09:00')}"></div>
      <div><label class="mag-modal__label">To</label><input class="mag-modal__input flow-f-end" type="time" value="${esc(step.step_config?.end || '17:00')}"></div>
      <div style="flex:0 0 auto"><label class="mag-modal__label">Slot</label>
        <select class="mag-modal__select flow-f-mins">${[...new Set([15, 30, 45, 60, Number(step.step_config?.mins) || 30])].sort((a, b) => a - b).map((n) => `<option value="${n}"${Number(step.step_config?.mins || 30) === n ? ' selected' : ''}>${n} min</option>`).join('')}</select></div>
    </div>
    <p class="mag-modal__hint">Visitors pick a day + time within these hours; the choice saves to the field above.</p>` : ''}
  </div>`;
}

function previewHTML(step) {
  const firstPrompt = step.step_prompts.find((p) => String(p).trim() !== '') || '';
  const promptText = firstPrompt
    ? `<div class="flow-node__prompt">“${esc(firstPrompt)}”</div>`
    : `<div class="flow-node__prompt is-empty">${step.step_type === 'message' ? 'No message yet' : 'No question yet'}</div>`;
  let extra = '';
  if (needsOptions(step.step_type)) {
    const labels = step.step_options.map((o) => String(o.label).trim()).filter(Boolean);
    if (labels.length) {
      const shown = labels.slice(0, 4).map((l) => `<span class="flow-node__chip">${esc(l)}</span>`).join('');
      const more = labels.length > 4 ? `<span class="flow-node__chip">+${labels.length - 4}</span>` : '';
      extra = `<div class="flow-node__chips">${shown}${more}</div>`;
    }
  } else if (needsField(step.step_type) && step.step_columns[0]) {
    const fieldLabel = LEAD_CAPTURE_FIELDS.find((f) => f.value === step.step_columns[0])?.label || step.step_columns[0];
    extra = `<div class="flow-node__meta">Saves to <b>${esc(fieldLabel)}</b></div>`;
  } else if (canBeTerminal(step.step_type) && step.step_config?.terminal) {
    const label = String(step.step_config?.cta?.label || '').trim();
    extra = `<div class="flow-node__meta">Ends the conversation${label ? ` — link: <b>${esc(label)}</b>` : ''}</div>`;
  }
  return `<div class="flow-node__preview">${promptText}${extra}</div>`;
}

function nodeHTML(step, i, total) {
  const m = meta(step.step_type);
  const open = step._uid === openUid;
  const style = `--nc:${m.color};--nct:${hexToRgba(m.color, 0.14)}`;
  return `<div class="flow-node${open ? ' flow-node--open' : ''}" data-uid="${step._uid}" style="${style}"${open ? '' : ' draggable="true"'}>
    <div class="flow-node__hd">
      <span class="flow-node__grip" title="${open ? '' : 'Drag to reorder'}"><span class="material-symbols-outlined" aria-hidden="true">${open ? 'expand_less' : 'drag_indicator'}</span></span>
      <span class="flow-node__ico"><span class="material-symbols-outlined" aria-hidden="true">${m.icon}</span></span>
      <span class="flow-node__type">${esc(m.label)}</span>
      <span class="flow-node__n">Step ${i + 1}</span>
      <span class="flow-node__tools">
        <button type="button" data-x="up" title="Move up"${i === 0 ? ' disabled' : ''}><span class="material-symbols-outlined" aria-hidden="true">arrow_upward</span></button>
        <button type="button" data-x="down" title="Move down"${i === total - 1 ? ' disabled' : ''}><span class="material-symbols-outlined" aria-hidden="true">arrow_downward</span></button>
        <button type="button" data-x="del" title="Delete step"${total === 1 ? ' disabled' : ''}><span class="material-symbols-outlined" aria-hidden="true">delete</span></button>
      </span>
    </div>
    ${open ? editorBodyHTML(step) : previewHTML(step)}
  </div>`;
}

const CONNECTOR = `<div class="flow-conn"><span class="flow-conn__line"></span><span class="material-symbols-outlined" aria-hidden="true">expand_more</span></div>`;

function renderNodes({ notifySelection = true } = {}) {
  const canvas = document.getElementById('flow-nodes');
  if (!canvas) return;
  const focused = document.activeElement;
  const focusUid = focused?.closest('.flow-node')?.dataset.uid;
  const focusSelector = focused?.dataset.x ? `[data-x="${focused.dataset.x}"]`
    : ['flow-f-type', 'flow-f-terminal'].find(name => focused?.classList.contains(name));
  if (flowOptions.progressive) rememberFlowDisclosures(canvas, disclosureState);
  const total = stepsModel.length;
  const parts = ['<div class="flow-start"><span class="material-symbols-outlined" aria-hidden="true">bolt</span> Conversation starts</div>', CONNECTOR];
  stepsModel.forEach((s, i) => {
    parts.push(nodeHTML(s, i, total));
    if (i < total - 1) parts.push(CONNECTOR);
  });
  canvas.innerHTML = parts.join('');
  canvas.hidden = false;
  if (flowOptions.progressive) {
    enhanceFlowDisclosures(canvas, stepsModel, disclosureState, flowOptions);
    const focused = !!openUid;
    canvas.classList.toggle('flow-canvas--focused', focused);
    for (const node of canvas.querySelectorAll('.flow-node')) node.hidden = focused && node.dataset.uid !== openUid;
    const panel = canvas.closest('.flow-workspace');
    if (panel) {
      panel.querySelector('[data-flow-back]').hidden = !focused;
      panel.querySelector('[data-flow-add]').hidden = focused;
      panel.querySelector('[data-flow-overview-help]').hidden = focused;
      panel.querySelector('[data-flow-outline-tools]').hidden = focused;
      panel.querySelector('[data-flow-question-nav]').hidden = !focused;
      panel.querySelector('[data-flow-insert]').hidden = !focused;
      const selectedIndex = stepsModel.findIndex(step => step._uid === openUid);
      panel.querySelector('[data-flow-prev]').disabled = selectedIndex <= 0;
      panel.querySelector('[data-flow-next]').disabled = selectedIndex >= total - 1;
      panel.querySelector('[data-flow-jump]').innerHTML = stepsModel.map((step, i) => `<option value="${step._uid}"${step._uid === openUid ? ' selected' : ''}>${i + 1}. ${esc(flowStepTitle(step))}</option>`).join('');
      const location = panel.querySelector('[data-flow-location]');
      location.textContent = focused ? `Editing question ${stepsModel.findIndex(step => step._uid === openUid) + 1} of ${total}`
        : `${total} ${total === 1 ? 'question' : 'questions'} in this conversation`;
      // Adding a step or navigating to a question ends the picker interaction.
      panel.querySelector('.flow-palette').hidden = true;
      panel.querySelector('[data-flow-add]').setAttribute('aria-expanded', 'false');
      panel.querySelector('[data-flow-insert]').setAttribute('aria-expanded', 'false');
      refreshOutline(panel);
    }
    if (focusUid && focusSelector) {
      const node = canvas.querySelector(`[data-uid="${focusUid}"]`);
      const target = node?.querySelector(focusSelector.startsWith('[') ? focusSelector : `.${focusSelector}`);
      const actions = target?.closest('.flow-card-actions');
      if (actions && !target.disabled) actions.open = true;
      (target?.disabled ? node?.querySelector('.flow-node__toggle') : target)?.focus({ preventScroll: true });
    }
    if (notifySelection) flowOptions.onSelectionChange?.();
  }
}

function refreshOutline(panel) {
  if (!flowOptions.progressive) return;
  const query = panel.querySelector('[data-flow-search]').value;
  const jump = panel.querySelector('[data-flow-jump]');
  [...jump.options].forEach((option, index) => { option.textContent = `${index + 1}. ${flowStepTitle(stepsModel[index])}`; });
  let attentionCount = 0, visibleCount = 0;
  stepsModel.forEach(step => {
    const hints = flowStepHints(step, stepsModel);
    if (hints.length) attentionCount++;
    const node = panel.querySelector(`[data-uid="${step._uid}"]`);
    let badge = node.querySelector('[data-flow-attention]');
    if (!badge) { badge = document.createElement('p'); badge.dataset.flowAttention = ''; badge.className = 'flow-attention'; node.querySelector('.flow-node__hd').after(badge); }
    badge.textContent = hints.join(' · '); badge.hidden = !hints.length;
    if (!openUid) {
      node.hidden = !matchesFlowSearch(step, query) || (outlineAttentionOnly && !hints.length);
      if (!node.hidden) visibleCount++;
    }
  });
  panel.querySelector('[data-flow-filter="attention"]').textContent = `Needs attention (${attentionCount})`;
  panel.querySelectorAll('[data-flow-filter]').forEach(button => button.setAttribute('aria-pressed', String((button.dataset.flowFilter === 'attention') === outlineAttentionOnly)));
  const branches = stepsModel.reduce((sum, step) => sum + step.step_options.filter(option => option.goto).length, 0);
  panel.querySelector('[data-flow-structure]').textContent = `${stepsModel.length} questions${branches ? ` · ${branches} branching ${branches === 1 ? 'choice' : 'choices'}` : ' · In order'}`;
  const empty = panel.querySelector('[data-flow-no-results]');
  empty.hidden = !!openUid || visibleCount > 0;
  empty.textContent = outlineAttentionOnly && !query.trim() ? 'No unfinished wording, fields or choice destinations found. Test the conversation before publishing.' : 'No matching questions. Try different wording or clear the filter.';
}

// Sync the currently-open node's live inputs back into the model (only the open card
// has editable fields; collapsed cards render from the model).
function readModel() {
  const el = document.querySelector('.flow-node--open');
  if (!el) return;
  const step = stepsModel.find((s) => s._uid === el.dataset.uid);
  if (!step) return;
  const typeEl = el.querySelector('.flow-f-type');
  if (typeEl && typeEl.value) step.step_type = typeEl.value;
  const promptsEl = el.querySelector('.flow-f-prompts');
  if (promptsEl && promptsEl.value !== step.step_prompts.join('\n')) step.step_prompts = promptsEl.value.split('\n');
  const fieldEl = el.querySelector('.flow-f-field');
  if (fieldEl && fieldEl.value !== (step.step_columns[0] || '')) {
    if (flowOptions.answerAuthoring && fieldEl.value === '__additional_answer__') {
      const answer = createAdditionalAnswer(step, crypto.randomUUID());
      step.step_columns = [answer.key]; step.step_answer = answer.metadata;
      fieldEl.add(new Option('Additional answer — keep with this inquiry', answer.key));
      fieldEl.value = answer.key;
    } else {
      if (step.step_answer?.id) [...fieldEl.options].find(option => option.value === step.step_columns[0])?.remove();
      step.step_columns = fieldEl.value.trim() ? [fieldEl.value.trim()] : [];
      delete step.step_answer;
    }
  }
  if (flowOptions.answerAuthoring && step.step_answer?.label_mode === 'auto') step.step_answer.label = automaticAnswerLabel(step);
  if (needsOptions(step.step_type)) {
    const prev = step.step_options;
    const newOpts = [];
    el.querySelectorAll('.flow-opt').forEach((oEl, k) => {
      newOpts.push({
        ...(prev[k] || {}),
        label: oEl.querySelector('.flow-o-label')?.value ?? '',
        icon: oEl.querySelector('.flow-o-icon')?.value ?? '',
        goto: oEl.querySelector('.flow-o-goto')?.value ?? prev[k]?.goto ?? '',
        description: oEl.querySelector('.flow-o-description')?.value ?? prev[k]?.description ?? '',
      });
    });
    step.step_options = newOpts;
  } else {
    step.step_options = [];
  }
  for (const [selector, key, checkbox] of [
    ['.flow-f-group', 'group_with_previous', true], ['.flow-f-group-heading', 'group_heading', false],
    ['.flow-f-optional', 'optional', true], ['.flow-f-prefilled', 'skip_if_prefilled', true],
    ['.flow-f-overwrite', 'allow_overwrite', true], ['.flow-f-skip-label', 'skip_label', false],
    ['.flow-f-placeholder', 'placeholder', false], ['.flow-f-presentation', 'presentation', false],
  ]) {
    const control = el.querySelector(selector);
    if (!control) continue;
    const before = checkbox ? !!step.step_config?.[key] : step.step_config?.[key] || '';
    const after = checkbox ? control.checked : control.value;
    if (before === after) continue;
    // A select's "Automatic" (empty) means "no override": drop the key rather than
    // storing '' — the SDK treats an unknown value as automatic anyway, but an
    // inert key would ride along in every saved package.
    if (key === 'presentation' && !after) step.step_config = configWithout(step.step_config, [key]);
    else step.step_config = { ...(step.step_config || {}), [key]: after };
  }
  if (needsSchedule(step.step_type)) {
    const mt = el.querySelector('.flow-f-mtlabel');
    const st = el.querySelector('.flow-f-start');
    const en = el.querySelector('.flow-f-end');
    const mn = el.querySelector('.flow-f-mins');
    step.step_config = {
      ...(step.step_config || defaultSchedule()),
      ...(mt && mt.value !== (step.step_config?.meeting_label || '') ? { meeting_label: mt.value.trim() } : {}),
      ...(st && st.value ? { start: st.value } : {}),
      ...(en && en.value ? { end: en.value } : {}),
      ...(mn && mn.value !== String(step.step_config?.mins || 30) ? { mins: parseInt(mn.value, 10) || 30 } : {}),
    };
  } else if (canBeTerminal(step.step_type)) {
    // REGRESSION GUARD: readModel() runs on every interaction with the open card
    // (moving it, adding/removing a choice, dragging, even just closing it) — not
    // only on an actual type change. The old code unconditionally set
    // step_config = null here for every non-scheduler type, which silently wiped
    // a message step's terminal/cta on the NEXT unrelated interaction after
    // setting it, with no type change involved at all. The terminal checkbox only
    // exists in the DOM once the editor body has actually rendered the message-
    // type fields — absent (e.g. mid a type-change this same tick, before
    // renderNodes() re-renders) means don't touch step_config here; reconcileStep()
    // (called right after this, only on an actual type change) owns that case.
    const termEl = el.querySelector('.flow-f-terminal');
    if (termEl) {
      const labelEl = el.querySelector('.flow-f-cta-label');
      const urlEl = el.querySelector('.flow-f-cta-url');
      // Keep the raw typed values in the MODEL (trimming/omitting an incomplete
      // cta happens in toPayload) so a half-entered label survives the re-render
      // this function triggers on every keystroke.
      // Unchecking ends terminal-ness ONLY — the rest of the bag
      // (placeholder / optional / skip_if_prefilled) belongs to features this
      // editor has no UI for, and nulling it here would be the same silent
      // data loss the step_config serializer fix removed.
      const rest = configWithout(step.step_config, TERMINAL_KEYS) || {};
      step.step_config = termEl.checked
        ? { ...rest, terminal: true, cta: { label: labelEl ? labelEl.value : '', url: urlEl ? urlEl.value : '' } }
        : step.step_config?.terminal ? configWithout(step.step_config, TERMINAL_KEYS) : step.step_config;
    }
  }
}

function clearDrop() {
  document.querySelectorAll('.flow-node.drop-before, .flow-node.drop-after')
    .forEach((n) => n.classList.remove('drop-before', 'drop-after'));
}
function cleanupDrag() {
  clearDrop();
  document.querySelectorAll('.flow-node.dragging').forEach((n) => n.classList.remove('dragging'));
  dragUid = null;
  dropInfo = null;
}

// Returns null when the flow is valid, else { message, uid, field }.
//
// `uid`/`field` exist so the caller can put the person IN FRONT OF the problem rather
// than only describing it — showFlowRoute() opens that step and marks that field.
// Returning a bare string (as this used to) left printing the sentence somewhere and
// hoping it was seen as the only recourse, which is how a working validation ended up
// invisible in production.
function validate(model = stepsModel) {
  if (!model.length) return { message: 'Add at least one step.', uid: null, field: null };
  const graphProblem = validateMagnetFlow(serializeSteps(model));
  if (graphProblem) return { ...graphProblem, uid: model[graphProblem.index]?._uid };
  for (let i = 0; i < model.length; i++) {
    const s = model[i];
    const n = i + 1;
    const at = (message, field = null) => ({ message, uid: s._uid, field });
    if (!s.step_prompts.some((p) => String(p).trim() !== '')) return at(`Step ${n}: add what the magnet should say.`, 'prompts');
    if (needsOptions(s.step_type) && !s.step_options.some((o) => String(o.label).trim() !== '')) return at(`Step ${n}: add at least one choice.`, 'options');
    if (needsField(s.step_type) && !needsOptions(s.step_type) && !s.step_columns.length) return at(`Step ${n}: choose which field this answer saves to.`, 'field');
    if (canBeTerminal(s.step_type) && s.step_config?.terminal) {
      // Mirrors the SDK's own renderTerminal() check (if (!url || !label) return —
      // it silently skips the button rather than rendering a broken one). Catching
      // a half-filled CTA here means the person building the flow finds out now,
      // not by publishing and finding the button silently isn't there.
      const label = String(s.step_config.cta?.label || '').trim();
      const url = String(s.step_config.cta?.url || '').trim();
      if (Boolean(label) !== Boolean(url)) {
        return at(`Step ${n}: fill in both the link label and URL, or leave both blank.`, 'cta');
      }
    }
  }
  return null;
}

// ── the CTA label/URL pair: say it while they type, not on save ──────────────
// The pair is all-or-nothing (see validate above), but each field reads "optional" on
// its own, so a half-filled pair looks finished until Save rejects it. Once EITHER
// side has content the pair is in play, and this says so inline.
//
// Runs on `input` and deliberately does NOT re-render the node: the canvas only
// re-renders on a type or terminal-checkbox change, and re-rendering mid-keystroke
// would drop focus. This only toggles classes and text on nodes already in the DOM.
function syncCtaPairHint(nodeEl) {
  if (!nodeEl) return;
  const labelEl = nodeEl.querySelector('.flow-f-cta-label');
  const urlEl = nodeEl.querySelector('.flow-f-cta-url');
  if (!labelEl || !urlEl) return;
  const hasLabel = labelEl.value.trim() !== '';
  const hasUrl = urlEl.value.trim() !== '';
  const engaged = hasLabel || hasUrl;   // the pair is now in play
  const halfDone = hasLabel !== hasUrl; // ...and only one side is filled

  nodeEl.querySelectorAll('.flow-cta-req').forEach((el) => {
    el.textContent = engaged ? 'required' : 'optional';
    el.classList.toggle('flow-sub--req', engaged);
  });
  // Mark only the side actually missing, not both.
  labelEl.classList.toggle('flow-f-invalid', halfDone && !hasLabel);
  urlEl.classList.toggle('flow-f-invalid', halfDone && !hasUrl);
  const warn = nodeEl.querySelector('.flow-cta-warn');
  if (warn) warn.hidden = !halfDone;
}

function serializeSteps(model = stepsModel) {
  let max = 0;
  model.forEach((s) => { const n = parseInt(s.id, 10); if (!isNaN(n) && n > max) max = n; });
  return model.map((s) => {
    const id = s.id && String(s.id).trim() ? String(s.id) : (s.id = String(++max));
    const options = needsOptions(s.step_type)
      ? s.step_options.map((o) => {
          const out = { ...o, label: String(o.label).trim(), goto: String(o.goto || '') };
          if (o.icon) out.icon = String(o.icon);
          return out;
        }).filter((o) => flowOptions.retainIncomplete || o.label !== '')
      : [];
    const out = {
      id,
      step_type: s.step_type,
      step_prompts: s.step_prompts.map((p) => String(p)).filter((p) => p.trim() !== ''),
      step_options: options,
      step_columns: s.step_columns.slice(),
      step_description: s.step_description || '',
    };
    if (s.step_answer !== undefined) {
      out.step_answer = structuredClone(s.step_answer);
      if (out.step_answer && !Array.isArray(out.step_answer) && typeof out.step_answer.label === 'string') {
        out.step_answer.label = out.step_answer.label.trim();
      }
    }
    // THE regression this file's fix is about: gating the whole bag on needsSchedule()
    // meant every non-scheduler step was serialized with step_config omitted entirely,
    // so the server (whose normSteps only copies step_config when the client sends one)
    // wrote the step back without it. Opening a magnet's edit modal and pressing Save —
    // no flow interaction required — was enough to erase every authored terminal CTA.
    if (needsSchedule(s.step_type)) {
      if (s.step_config) out.step_config = { ...defaultSchedule(), ...s.step_config };
    } else if (s.step_config && Object.keys(s.step_config).length) {
      const cfg = { ...s.step_config };
      // Normalize the terminal CTA at SERIALIZE time, not read time: the model holds
      // whatever is currently typed so a half-entered label survives re-renders, but
      // what we persist only carries `cta` when BOTH parts are set — matching the SDK's
      // own `if (!url || !label) return`. A terminal step with no CTA is valid; it just
      // ends the conversation. validate() already blocks saving with exactly one filled.
      if (canBeTerminal(s.step_type) && cfg.terminal) {
        const label = String(cfg.cta?.label ?? '').trim();
        const url = String(cfg.cta?.url ?? '').trim();
        if ((label && url) || flowOptions.retainIncomplete) cfg.cta = { label, url };
        else delete cfg.cta;
      }
      out.step_config = cfg;
    }
    return out;
  });
}

function toPayload(model = stepsModel) {
  return serializeSteps(model).map((step, i) => {
    const source = model[i];
    if (!source._original) return step;
    const result = mergeEdited(source._original, source._baseline, step);
    // Explicit deletion distinguishes a deliberately cleared bag from an old
    // client that never knew about it and simply omitted it.
    if (source._original.step_config && !Object.hasOwn(result, 'step_config')) result.step_config = null;
    return result;
  });
}

// ── mount into the edit modal's Flow tab ─────────────────────────────────────
// One flow editor at a time (module-level working state). Renders the route
// selector + node canvas + palette into `panelEl`; the host edit modal owns the
// footer (Save/Cancel/Preview) and calls validateFlows / saveFlows / currentChatSteps.
export function mountFlowTab(panelEl, magnet, onDirty, options = {}) {
  ensureStyles();
  options = { ...options, assistantAuthoring: options.assistantAuthoring ?? magnet.assistant_authoring?.available === true, answerAuthoring: options.answerAuthoring ?? magnet.answer_authoring?.available === true };
  flowOptions = options;
  panelEl.classList.toggle('flow-workspace', !!options.progressive);
  disclosureState = new Map();
  routeOpenSteps = {};
  outlineAttentionOnly = false; insertAfterUid = null;
  flowMagnet = magnet;
  flowOnDirty = typeof onDirty === 'function' ? onDirty : null;
  // Editable routes: the chat/default flow, plus book/support when the magnet has those CTAs.
  flowRouteKeys = ['chat', ...['book', 'support'].filter((r) => (magnet.actions || []).some((a) => a && a.type === r))];
  flowRouteModels = { chat: loadRouteModel('chat', magnet) };
  flowBaselines = { chat: toPayload(flowRouteModels.chat) };
  currentRoute = 'chat';
  stepsModel = flowRouteModels.chat;
  openUid = stepsModel.length === 1 ? stepsModel[0]._uid : null; // one step ⇒ start expanded
  dragUid = null;
  dropInfo = null;

  panelEl.innerHTML = `
    ${options.progressive ? `<div class="flow-workspace__bar"><button type="button" class="flow-workspace__back" data-flow-back hidden><span class="material-symbols-outlined" aria-hidden="true">arrow_back</span> Back to questions</button><span data-flow-location></span></div>
    <p class="mag-modal__hint" data-flow-overview-help>Choose Edit question to change its wording, answers or next step. Use Actions to move or delete it.</p>
    <div class="flow-outline-tools" data-flow-outline-tools><p data-flow-structure></p><label class="flow-sr-only" for="flow-search">Find a question, choice or answer field</label><input type="search" id="flow-search" data-flow-search placeholder="Find a question, choice or field…"><div class="flow-outline-filters"><button type="button" data-flow-filter="all" aria-pressed="true">All questions</button><button type="button" data-flow-filter="attention" aria-pressed="false">Needs attention</button></div></div>
    <nav class="flow-question-nav" data-flow-question-nav aria-label="Question navigation" hidden><button type="button" data-flow-prev aria-label="Previous question">←</button><label class="flow-sr-only" for="flow-jump">Jump to question</label><select id="flow-jump" data-flow-jump></select><button type="button" data-flow-next aria-label="Next question">→</button></nav>` : '<p class="mag-modal__hint" style="margin-top:0">Each card is one thing your magnet asks. Drag a card to reorder, click to edit, add steps from the palette below.</p>'}
    ${flowRouteKeys.length > 1 || options.progressive ? `<div class="flow-routes"><label class="flow-routes__lbl" for="flow-route">${options.progressive ? 'Visitor path' : 'Editing flow'}</label><select class="mag-modal__select" id="flow-route">${flowRouteKeys.map((r) => `<option value="${r}">${esc(ROUTE_LABEL[r] || r)}</option>`).join('')}</select></div>` : ''}
    ${options.progressive ? '<p class="mag-modal__hint" id="flow-path-note"></p><p class="flow-sr-only" id="flow-announcement" role="status"></p>' : ''}
    <p class="mag-modal__error" id="flow-error" role="alert"></p>
    <div class="flow-canvas" id="flow-nodes"></div>
    ${options.progressive ? '<p class="flow-no-results" data-flow-no-results hidden></p><button type="button" class="flow-workspace__add" data-flow-add aria-expanded="false" aria-controls="flow-palette"><span class="material-symbols-outlined" aria-hidden="true">add</span> Add a question</button><button type="button" class="flow-workspace__add" data-flow-insert aria-expanded="false" aria-controls="flow-palette" hidden>＋ Insert a question after this</button>' : ''}
    <div class="flow-palette"${options.progressive ? ' hidden' : ''}>
      <div class="flow-palette__heading"><p class="flow-palette__t">${options.progressive ? 'What would you like to add?' : 'Add a step'}</p>${options.progressive ? '<button type="button" class="flow-workspace__cancel" data-flow-cancel-add>Cancel</button>' : ''}</div>
      ${options.progressive ? '<p class="flow-insert-note" data-flow-insert-note hidden>Some choices jump to other questions. Adding here keeps those destinations. To include this question on a branching path, update the preceding question’s Choices &amp; paths.</p>' : ''}
      <div class="flow-palette__grid" id="flow-palette">
        ${TYPE_ORDER.map((t) => { const m = meta(t), purpose = QUESTION_PURPOSE[t]; return `<button class="flow-palette__chip" type="button" data-type="${t}" style="--pc:${m.color}"><span class="material-symbols-outlined" aria-hidden="true">${m.icon}</span>${options.progressive && purpose ? `<span><strong>${esc(purpose[0])}</strong><small>${esc(purpose[1])}</small></span>` : esc(m.label)}</button>`; }).join('')}
      </div>
    </div>`;
  renderNodes();
  wireFlowEditor(panelEl);
  if (options.progressive) syncFlowPaths(magnet);
}

export const currentFlowRoute = () => currentRoute;

// Configuration readback must not rebuild controls or dirty the definition.
export function refreshFlowDeliveryContext(context) {
  if (!flowOptions.progressive || context?.ownerGuid !== flowMagnet.owner_guid || context?.magnetGuid !== flowMagnet.guid) return;
  const node = document.querySelector('.flow-node--open');
  const step = stepsModel.find(item => item._uid === node?.dataset.uid);
  if (node && step) updateAnswerDelivery(node, step, flowOptions);
}

export function currentFlowNavigation() {
  readModel();
  return { route: currentRoute, uid: openUid || '',
    routes: flowRouteKeys.map(route => ({ value: route, label: [...(document.getElementById('flow-route')?.options || [])].find(option => option.value === route)?.textContent || ROUTE_LABEL[route] || route })),
    questions: stepsModel.map((step, index) => ({ uid: step._uid, label: `${index + 1}. ${flowStepTitle(step)}` })) };
}

// Canvas navigation selects existing authoring controls without validation errors,
// dirtying the draft, or transferring keyboard focus away from the canvas.
export function selectFlowQuestion(route, uid = null, { index = null, silent = false, total = null } = {}) {
  if (!flowRouteKeys.includes(route)) return false;
  readModel();
  if (!flowRouteModels[route]) {
    flowRouteModels[route] = loadRouteModel(route, flowMagnet);
    flowBaselines[route] = toPayload(flowRouteModels[route]);
  }
  const model = flowRouteModels[route];
  if (index !== null) {
    if (!Number.isInteger(index) || !model[index] || (total !== null && total !== model.length)) return false;
    uid = model[index]._uid;
  }
  if (uid && !model.some(step => step._uid === uid)) return false;
  if (silent && route === currentRoute && uid === openUid) return true;
  currentRoute = route; stepsModel = model;
  openUid = uid || model[0]?._uid || null;
  const selector = document.getElementById('flow-route');
  if (selector) selector.value = route;
  renderNodes({ notifySelection: !silent }); updatePathNote();
  document.getElementById('flow-nodes')?.closest('.mag-modal__body')?.scrollTo?.({ top: 0 });
  if (!silent) flowOptions.onRouteChange?.(route);
  return true;
}

// Selection is authoring state, separate from visitor navigation and saved data.
export function currentFlowSelection() {
  readModel();
  const index = stepsModel.findIndex(step => step._uid === openUid);
  if (index < 0) return null;
  return { uid: openUid, route: currentRoute, index, total: stepsModel.length,
    step: structuredClone(toPayload(stepsModel)[index]) };
}
export function syncFlowPaths(magnet) {
  if (!flowOptions.progressive) return;
  readModel();
  const previousActions = flowMagnet.actions || [];
  flowMagnet = { ...flowMagnet, actions: magnet.actions, home: magnet.home };
  for (const route of ['book', 'support']) {
    const model = flowRouteModels[route];
    const before = previousActions.find(action => action.type === route);
    const after = (magnet.actions || []).find(action => action.type === route);
    // An untouched fallback should reflect changed welcome/provider settings.
    // Authored or deliberately edited paths keep their own questions verbatim.
    if (model && after && before && !sameValue(before.config, after.config) && !flowMagnet.routes?.[route]?.length
      && sameValue(toPayload(model), flowBaselines[route])) {
      flowRouteModels[route] = loadRouteModel(route, flowMagnet);
      flowBaselines[route] = toPayload(flowRouteModels[route]);
      if (currentRoute === route) { stepsModel = flowRouteModels[route]; openUid = null; renderNodes(); }
    }
  }
  flowRouteKeys = ['chat', ...['book', 'support'].filter(route => (magnet.actions || []).some(action => action.type === route)
    || flowMagnet.routes?.[route]?.length || flowRouteModels[route])];
  const selector = document.getElementById('flow-route');
  if (!selector) return;
  const label = { chat: 'Main conversation', book: 'Appointments', support: 'Support' };
  selector.innerHTML = flowRouteKeys.map(route => {
    const visible = route === 'chat' && (!magnet.home || !magnet.actions?.length) || (magnet.actions || []).some(action => action.type === route);
    return `<option value="${route}">${label[route]}${visible ? '' : ' · not offered to visitors'}</option>`;
  }).join('');
  selector.value = currentRoute;
  selector.closest('.flow-routes').hidden = flowRouteKeys.length < 2;
  updatePathNote();
}
function updatePathNote() {
  const note = document.getElementById('flow-path-note');
  if (!note) return;
  const source = (flowMagnet.actions || []).find(action => action.type === 'book')?.config?.source;
  const external = currentRoute === 'book' && ['calendly', 'calcom'].includes(source);
  note.textContent = external ? 'Your booking provider handles this path. These questions are kept for the built-in appointment request.'
    : document.getElementById('flow-route')?.selectedOptions[0]?.textContent.includes('not offered')
      ? 'Visitors cannot choose this path. Its questions and settings are kept.' : '';
  note.hidden = !note.textContent;
}

function markFlowDirty() { if (flowOnDirty) flowOnDirty(); }
function clearFlowErr() {
  const el = document.getElementById('flow-error'); if (el) el.textContent = '';
  for (const control of document.querySelectorAll('#flow-nodes [aria-invalid="true"]')) {
    control.removeAttribute('aria-invalid');
    const ids = (control.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== 'flow-error');
    if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));
    else control.removeAttribute('aria-describedby');
  }
}

// Wire the mounted editor's interactions to the module working state.
function wireFlowEditor(root) {
  const canvas = root.querySelector('#flow-nodes');
  const routeSel = root.querySelector('#flow-route');

  // Switch which CTA route we're editing — stash the current model, load/seed the target.
  routeSel?.addEventListener('change', () => {
    readModel();
    routeOpenSteps[currentRoute] = openUid;
    currentRoute = routeSel.value;
    if (flowOptions.progressive) { root.querySelector('[data-flow-search]').value = ''; outlineAttentionOnly = false; }
    if (!flowRouteModels[currentRoute]) {
      flowRouteModels[currentRoute] = loadRouteModel(currentRoute, flowMagnet);
      flowBaselines[currentRoute] = toPayload(flowRouteModels[currentRoute]);
    }
    stepsModel = flowRouteModels[currentRoute];
    openUid = routeOpenSteps[currentRoute] || (stepsModel.length === 1 ? stepsModel[0]._uid : null);
    clearFlowErr();
    renderNodes();
    updatePathNote();
    flowOptions.onRouteChange?.(currentRoute);
  });

  const openStep = (targetUid) => {
    clearFlowErr();
    const wasOpen = openUid;
    readModel();
    openUid = openUid === targetUid ? null : targetUid;
    // Closing (or switching away from) a card is the natural "done editing this
    // step" moment — refresh the live preview once here rather than per field
    // blur (see the canvas 'change' listener below for why per-field doesn't work).
    if (wasOpen && (!flowOptions.progressive || !sameValue(toPayload(stepsModel), flowBaselines[currentRoute]))) markFlowDirty();
    renderNodes();
    if (flowOptions.progressive) {
      const node = canvas.querySelector(`[data-uid="${targetUid}"]`);
      if (openUid) root.closest('.mag-modal__body')?.scrollTo?.({ top: 0 });
      (node?.hidden ? root.querySelector('[data-flow-search]') : node?.querySelector(openUid ? '.flow-f-prompts' : '.flow-node__toggle'))?.focus({ preventScroll: true });
    }
  };
  root.querySelector('[data-flow-back]')?.addEventListener('click', () => { if (openUid) openStep(openUid); });
  root.querySelector('[data-flow-search]')?.addEventListener('input', event => { event.stopPropagation(); refreshOutline(root); });
  root.querySelector('[data-flow-search]')?.addEventListener('change', event => event.stopPropagation());
  root.querySelector('[data-flow-jump]')?.addEventListener('input', event => event.stopPropagation());
  root.querySelectorAll('[data-flow-filter]').forEach(button => button.addEventListener('click', () => {
    outlineAttentionOnly = button.dataset.flowFilter === 'attention'; refreshOutline(root);
  }));
  root.querySelector('[data-flow-jump]')?.addEventListener('change', event => {
    event.stopPropagation(); if (event.target.value !== openUid) openStep(event.target.value);
  });
  for (const [selector, offset] of [['[data-flow-prev]', -1], ['[data-flow-next]', 1]]) {
    root.querySelector(selector)?.addEventListener('click', () => {
      const target = stepsModel[stepsModel.findIndex(step => step._uid === openUid) + offset];
      if (target) openStep(target._uid);
    });
  }
  const togglePicker = (open) => {
    insertAfterUid = open ? openUid : null;
    root.querySelector('.flow-palette').hidden = !open;
    canvas.hidden = open;
    root.querySelector('[data-flow-add]').setAttribute('aria-expanded', String(open));
    root.querySelector('[data-flow-insert]').setAttribute('aria-expanded', String(open));
    root.querySelector('.flow-palette__t').textContent = openUid ? `What comes after question ${stepsModel.findIndex(step => step._uid === openUid) + 1}?` : 'What would you like to add?';
    const branches = stepsModel.find(step => step._uid === openUid)?.step_options.some(option => option.goto);
    root.querySelector('[data-flow-insert-note]').hidden = !open || !branches;
    root.querySelector('[data-flow-question-nav]').hidden = open || !openUid;
    (open ? root.querySelector('#flow-palette button') : root.querySelector(openUid ? '[data-flow-insert]' : '[data-flow-add]'))?.focus();
  };
  root.querySelector('[data-flow-add]')?.addEventListener('click', () => togglePicker(root.querySelector('.flow-palette').hidden));
  root.querySelector('[data-flow-insert]')?.addEventListener('click', () => togglePicker(root.querySelector('.flow-palette').hidden));
  root.querySelector('[data-flow-cancel-add]')?.addEventListener('click', () => togglePicker(false));
  root.querySelector('.flow-palette').addEventListener('keydown', event => {
    if (flowOptions.progressive && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); togglePicker(false); }
  });
  canvas.addEventListener('keydown', event => {
    if (flowOptions.progressive && event.key === 'Escape' && openUid && event.target.closest('.flow-node--open')) {
      event.preventDefault(); event.stopPropagation(); openStep(openUid);
    }
  });

  // clicks: tools, expand/collapse, add/remove choice
  canvas.addEventListener('click', (e) => {
    const toolBtn = e.target.closest('button[data-x]');
    const nodeEl = e.target.closest('.flow-node');
    if (!nodeEl) return;
    const i = stepsModel.findIndex((s) => s._uid === nodeEl.dataset.uid);
    if (i < 0) return;

    if (toolBtn) {
      const x = toolBtn.dataset.x;
      if (x === 'done') { openStep(nodeEl.dataset.uid); return; }
      clearFlowErr();
      readModel();
      if (x === 'up' && i > 0) { [stepsModel[i - 1], stepsModel[i]] = [stepsModel[i], stepsModel[i - 1]]; markFlowDirty(); renderNodes(); }
      else if (x === 'down' && i < stepsModel.length - 1) { [stepsModel[i + 1], stepsModel[i]] = [stepsModel[i], stepsModel[i + 1]]; markFlowDirty(); renderNodes(); }
      else if (x === 'del' && stepsModel.length > 1) {
        const referrer = stepsModel.find(s => s.step_options.some(o => String(o.goto || '') === stepsModel[i].id));
        if (referrer) {
          showFlowRoute(currentRoute, 'Choose a replacement destination for every choice pointing to this step before deleting it.', { uid: referrer._uid, field: 'options' });
          return;
        }
        if (openUid === nodeEl.dataset.uid) openUid = null;
        stepsModel.splice(i, 1); markFlowDirty(); renderNodes();
        if (flowOptions.progressive) canvas.querySelector(`[data-uid="${stepsModel[Math.min(i, stepsModel.length - 1)]._uid}"] .flow-node__toggle`)?.focus();
      }
      else if (x === 'addopt') { stepsModel[i].step_options.push({ label: '', goto: '', icon: '' }); markFlowDirty(); renderNodes(); if (flowOptions.progressive) canvas.querySelector('.flow-node--open .flow-opt:last-child .flow-o-label')?.focus(); }
      else if (x === 'delopt') {
        const optEl = toolBtn.closest('.flow-opt');
        const k = Array.from(optEl.parentNode.children).indexOf(optEl);
        if (stepsModel[i].step_options.length > 1 && k >= 0) { stepsModel[i].step_options.splice(k, 1); markFlowDirty(); renderNodes(); }
      }
      const announcement = root.querySelector('#flow-announcement');
      if (announcement && ['up', 'down'].includes(x)) announcement.textContent = `Question moved to position ${stepsModel.findIndex(step => step._uid === nodeEl.dataset.uid) + 1}.`;
      return;
    }
    // a click on the header or the preview (not inside the editor body) toggles the card
    if (e.target.closest('.flow-card-actions')) return;
    if (e.target.closest('.flow-node__body')) return;
    if (e.target.closest('.flow-node__hd') || e.target.closest('.flow-node__preview')) openStep(nodeEl.dataset.uid);
  });

  // Every field 'change' inside an open step (option labels, the "saves to"
  // field, prompts textarea, scheduler fields, the type select) is handled
  // here, not left to bubble to the host modal's listener. Reasoning: a step
  // typically has several short fields (a question, 2+ option labels, a
  // saves-to field) filled in sequence via Tab. Each field's blur fires its
  // own 'change' independently, spaced apart by however long it takes to type
  // the NEXT field's content — often 1-2+ seconds, far longer than any
  // reasonable debounce window could bridge. Left to bubble, that reproduces
  // the exact "pops in and out" symptom the message-field fix solved, just
  // spread across more fields instead of one. stopPropagation() here means
  // per-field edits only ever mark the model dirty (still real-time, via the
  // 'input' listener up in magnets-route.js, which is untouched by this) —
  // the actual preview refresh instead fires once when the card closes
  // (openStep, above), treating "editing this step" as one atomic unit.
  // Live all-or-nothing feedback on the CTA pair. Listens for `input` (not `change`)
  // so it reacts as they type rather than on blur, and never re-renders — see
  // syncCtaPairHint. Not stopPropagation'd: magnets-route.js's own debounced 'input'
  // listener still needs to see these keystrokes to mark the modal dirty.
  canvas.addEventListener('click', e => {
    if (!e.target.closest('[data-answer-label-reset]') || !flowOptions.answerAuthoring) return;
    const step = stepsModel.find(item => item._uid === openUid);
    if (!step || step.step_columns.length !== 1) return;
    step.step_answer = { ...(step.step_answer || { version: 1 }), label: automaticAnswerLabel(step), label_mode: 'auto' };
    const input = canvas.querySelector('[data-answer-label]');
    if (input) input.value = answerLabelForStep(step);
    markFlowDirty();
    updateFlowDisclosure(e.target.closest('.flow-node'), step, stepsModel, flowOptions);
    input?.focus({ preventScroll: true });
  });

  canvas.addEventListener('input', (e) => {
    if (e.target.matches('[data-answer-label]') && flowOptions.answerAuthoring) {
      const step = stepsModel.find(item => item._uid === openUid);
      if (step?.step_columns.length === 1) {
        step.step_answer = { ...(step.step_answer || { version: 1 }), label: e.target.value, label_mode: 'custom' };
        markFlowDirty();
      }
    }
    if (e.target.getAttribute('aria-invalid') === 'true') clearFlowErr();
    if (flowOptions.progressive) {
      readModel();
      updateFlowDisclosure(e.target.closest('.flow-node'), stepsModel.find(step => step._uid === openUid), stepsModel, flowOptions);
      refreshOutline(root);
    }
    if (!e.target.classList?.contains('flow-f-cta-label')
      && !e.target.classList?.contains('flow-f-cta-url')) return;
    // Typing into the field the save error pointed at is the fix in progress —
    // drop the stale message rather than leave it contradicting the live hint.
    clearFlowErr();
    syncCtaPairHint(e.target.closest('.flow-node'));
  });

  canvas.addEventListener('change', (e) => {
    e.stopPropagation();
    if (e.target.getAttribute('aria-invalid') === 'true') clearFlowErr();
    if (flowOptions.progressive) {
      readModel();
      updateFlowDisclosure(e.target.closest('.flow-node'), stepsModel.find(step => step._uid === openUid), stepsModel, flowOptions);
      refreshOutline(root);
    }
    if (e.target.classList.contains('flow-f-field')) { markFlowDirty(); return; }
    const isType = e.target.classList.contains('flow-f-type');
    // The terminal checkbox needs the same immediate re-render as the type
    // select (it changes which fields are visible — the label/URL inputs — not
    // just a value readModel() can pick up passively later like every other
    // field in this editor).
    const isTerminal = e.target.classList.contains('flow-f-terminal');
    if (!isType && !isTerminal) return;
    clearFlowErr();
    readModel();
    if (isType) {
      const step = stepsModel.find((s) => s._uid === e.target.closest('.flow-node')?.dataset.uid);
      if (step) reconcileStep(step);
    }
    markFlowDirty();
    renderNodes();
  });

  // ── native drag-to-reorder ──
  canvas.addEventListener('dragstart', (e) => {
    const node = e.target.closest('.flow-node');
    if (!node || node.getAttribute('draggable') !== 'true') return;
    dragUid = node.dataset.uid;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', dragUid); } catch { /* some browsers require a payload */ }
    requestAnimationFrame(() => node.classList.add('dragging'));
  });
  canvas.addEventListener('dragover', (e) => {
    if (!dragUid) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const node = e.target.closest('.flow-node');
    clearDrop();
    if (!node || node.dataset.uid === dragUid) { dropInfo = null; return; }
    const r = node.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    node.classList.add(after ? 'drop-after' : 'drop-before');
    dropInfo = { uid: node.dataset.uid, after };
  });
  canvas.addEventListener('drop', (e) => {
    if (!dragUid || !dropInfo) { cleanupDrag(); return; }
    e.preventDefault();
    clearFlowErr();
    readModel();
    const from = stepsModel.findIndex((s) => s._uid === dragUid);
    if (from < 0) { cleanupDrag(); return; }
    const [moved] = stepsModel.splice(from, 1);
    const to = stepsModel.findIndex((s) => s._uid === dropInfo.uid);
    if (to < 0) { stepsModel.splice(from, 0, moved); cleanupDrag(); return; }
    stepsModel.splice(dropInfo.after ? to + 1 : to, 0, moved);
    markFlowDirty();
    cleanupDrag();
    renderNodes();
  });
  canvas.addEventListener('dragend', cleanupDrag);

  // palette → append a step of the chosen type, expanded for editing
  root.querySelector('#flow-palette').addEventListener('click', (e) => {
    const chip = e.target.closest('[data-type]');
    if (!chip) return;
    clearFlowErr();
    readModel();
    const step = blankStep(chip.dataset.type);
    const after = insertAfterUid ? stepsModel.findIndex(item => item._uid === insertAfterUid) : -1;
    if (flowOptions.progressive && after >= 0) stepsModel.splice(after + 1, 0, step);
    else stepsModel.push(step);
    insertAfterUid = null;
    openUid = step._uid;
    markFlowDirty();
    renderNodes();
    canvas.querySelector('.flow-node--open')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    if (flowOptions.progressive) canvas.querySelector('.flow-node--open .flow-f-prompts')?.focus({ preventScroll: true });
  });
}

// Sync the open card into the model (host calls before Save/Preview).
export function syncFlow() { readModel(); }

// Validate every opened route. Returns { route, message } or null when all valid.
export function validateFlows() {
  readModel();
  for (const r of Object.keys(flowRouteModels)) {
    if (sameValue(toPayload(flowRouteModels[r]), flowBaselines[r]) && (r !== 'chat' || (flowMagnet.revision && flowMagnet.status !== 'DRAFT'))) continue;
    const problem = validate(flowRouteModels[r]);
    if (problem) {
      return {
        route: r,
        message: (flowRouteKeys.length > 1 ? `${ROUTE_LABEL[r] || r}: ` : '') + problem.message,
        uid: problem.uid,
        field: problem.field,
      };
    }
  }
  return null;
}

// Bring a route into view (used to jump to a validation error's route) + surface a message.
export function showFlowRoute(route, message, opts = {}) {
  const { uid = null, field = null } = opts;
  if (flowRouteModels[route]) {
    readModel();
    currentRoute = route;
    stepsModel = flowRouteModels[route];
    // Open the offending step instead of collapsing everything. Setting openUid to
    // null here (the old behaviour) closed the very card the message was about, so
    // even someone who found the text was then told "Step 3: ..." while looking at
    // three identical closed rows.
    openUid = uid && stepsModel.some((s) => s._uid === uid) ? uid : null;
    const sel = document.getElementById('flow-route');
    if (sel) sel.value = route;
    renderNodes();
    updatePathNote();
    flowOptions.onRouteChange?.(currentRoute);
  }
  const el = document.getElementById('flow-error');
  if (el) el.textContent = message || '';

  if (!uid) return;
  const node = document.querySelector(`.flow-node[data-uid="${uid}"]`);
  if (!node) return;
  // jsdom has no scrollIntoView; optional-call so tests (and any browser without it)
  // just skip the scroll rather than throwing past the field marking below.
  node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  if (field === 'terminal') {
    const control = node.querySelector('.flow-f-terminal');
    revealFlowControl(control);
  }
  if (field === 'cta') {
    syncCtaPairHint(node);
    // Focus whichever side is empty — that is the one they have to act on.
    const labelEl = node.querySelector('.flow-f-cta-label');
    const urlEl = node.querySelector('.flow-f-cta-url');
    const target = labelEl && labelEl.value.trim() === '' ? labelEl : urlEl;
    revealFlowControl(target);
  }
  if (field === 'options') {
    const empty = [...node.querySelectorAll('.flow-o-label')].find(input => !input.value.trim());
    const branch = [...node.querySelectorAll('.flow-o-goto')].find(select => select.value);
    revealFlowControl(empty || branch || node.querySelector('.flow-o-label'));
  }
  const selectors = { answer_label: '[data-answer-label]', field: '.flow-f-field', prompts: '.flow-f-prompts', allow_overwrite: '.flow-f-overwrite' };
  if (selectors[field]) revealFlowControl(node.querySelector(selectors[field]));
}

// Only deliberately changed routes participate in a combined save. Opening a
// fallback Book/Support tab must never create a stored route by itself.
export function changedFlowRoutes() {
  readModel();
  const changes = {};
  for (const [route, model] of Object.entries(flowRouteModels)) {
    const steps = toPayload(model);
    if (!sameValue(steps, flowBaselines[route])) changes[route] = steps.map((step, i) => ({ ...step, id: step.id || model[i].id }));
  }
  return changes;
}

export function currentFlowDefinition() {
  const changes = changedFlowRoutes();
  const routes = { ...(flowMagnet?.routes || {}) };
  for (const [route, steps] of Object.entries(changes)) if (route !== 'chat') routes[route] = steps;
  return { steps: changes.chat || flowMagnet?.steps || [], routes };
}

// Standalone flow saves use the same atomic revision-checked contract as the modal.
export async function saveFlows(magnetGuid) {
  if (!flowMagnet?.revision) throw new Error('Reload this magnet before saving.');
  const models = flowRouteModels;
  const flows = changedFlowRoutes();
  const chat = flows.chat || toPayload(models.chat || []);
  const submitted = Object.fromEntries(Object.entries(models).map(([route, model]) => [route, {
    raw: flows[route] || toPayload(model), projected: serializeSteps(model), uids: model.map(s => s._uid),
  }]));
  const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(magnetGuid)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ save_version: 1, expected_revision: flowMagnet.revision,
      steps: chat, flows }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok || !data.revision) throw new Error(data.message || data.error || 'Flow save could not be confirmed. Your changes are still here.');
  if (flowRouteModels !== models) return data; // another editor opened while awaiting the save
  readModel();
  const routes = { ...(flowMagnet.routes || {}) };
  for (const [route, steps] of Object.entries(flows)) if (route !== 'chat') routes[route] = steps;
  flowMagnet = { ...flowMagnet, revision: data.revision, steps: chat, routes };
  for (const [route, snapshot] of Object.entries(submitted)) {
    flowBaselines[route] = snapshot.raw;
    for (const step of models[route]) {
      const i = snapshot.uids.indexOf(step._uid);
      if (i < 0) continue;
      // Rebase only what was sent. Keep edits made while the request was pending,
      // and retain IDs that were materialized on the first legacy-route save.
      step._original = structuredClone(snapshot.raw[i]);
      step._baseline = structuredClone(snapshot.projected[i]);
    }
  }
  return data;
}

// Replace only the default route; retain the original baseline and other working routes.
export function replaceChatSteps(steps) {
  readModel();
  flowRouteModels.chat = modelFromSteps(structuredClone(steps));
  currentRoute = 'chat'; stepsModel = flowRouteModels.chat;
  openUid = null; routeOpenSteps.chat = null;
  renderNodes();
  showFlowRoute('chat');
  flowOnDirty?.();
}

export function currentChatSteps() { readModel(); return toPayload(flowRouteModels.chat || []); }
export default { mountFlowTab, syncFlow, validateFlows, showFlowRoute, saveFlows, currentChatSteps, changedFlowRoutes, currentFlowDefinition };
